/**
 * VectorForge "Add to cart"
 *
 * Progressive enhancement over a plain <form action="/cart/add"> that already
 * works without JavaScript (the browser lands on /cart via `return_to`).
 *
 * With JS we:
 *   1. drive the quantity stepper (− / +)
 *   2. add the variant × quantity via /cart/add.js without leaving the page
 *   3. refresh the "N licenses in your cart" status + the header cart icon
 */
(function () {
  if (window.__vfAddToCartBound) return;
  window.__vfAddToCartBound = true;

  const FORM = 'form[data-vf-add-to-cart]';

  /* ------------------------------------------------------------------ utils */

  function clampQty(input) {
    const min = Number(input.min) || 1;
    const max = Number(input.max) || Infinity;
    let value = parseInt(input.value, 10);
    if (!Number.isFinite(value)) value = min;
    value = Math.min(Math.max(value, min), max);
    input.value = String(value);
    return value;
  }

  async function requestJSON(url, options) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...options,
    });
    let data = null;
    try {
      data = await response.json();
    } catch (_) {
      /* ignore */
    }
    if (!response.ok) {
      const message = (data && (data.description || data.message)) || 'Something went wrong. Please try again.';
      throw new Error(message);
    }
    return data;
  }

  function setBusy(form, busy, message) {
    const button = form.querySelector('[data-vf-add-to-cart-button]');
    const label = form.querySelector('[data-vf-add-to-cart-label]');
    const error = form.parentElement && form.parentElement.querySelector('[data-vf-add-to-cart-error]');
    if (button) {
      button.setAttribute('aria-busy', busy ? 'true' : 'false');
      button.disabled = !!busy;
    }
    if (label) label.textContent = busy ? form.dataset.labelBusy || 'Adding…' : form.dataset.label || 'Add to cart';
    if (error) error.textContent = message || '';
  }

  function flashAdded(form) {
    const button = form.querySelector('[data-vf-add-to-cart-button]');
    const label = form.querySelector('[data-vf-add-to-cart-label]');
    if (!button || !label) return;
    button.classList.add('vf-add-to-cart__button--added');
    label.textContent = form.dataset.labelAdded || 'Added to cart';
    window.clearTimeout(button.__vfAddedTimer);
    button.__vfAddedTimer = window.setTimeout(() => {
      button.classList.remove('vf-add-to-cart__button--added');
      label.textContent = form.dataset.label || 'Add to cart';
    }, 2200);
  }

  /**
   * Update every "N licenses in your cart" status line on the page, plus the
   * theme's header cart bubble, from a fresh /cart.js payload.
   */
  function renderCart(cart) {
    const totalCount = cart && typeof cart.item_count === 'number' ? cart.item_count : 0;

    document.querySelectorAll('[data-vf-add-to-cart-status]').forEach((status) => {
      const form = status.parentElement && status.parentElement.querySelector(FORM);
      const variantId = Number(status.dataset.variantId);
      const text = status.querySelector('[data-vf-add-to-cart-status-text]');
      const one = (form && form.dataset.unitSingular) || 'license';
      const many = (form && form.dataset.unitPlural) || 'licenses';

      let count = 0;
      if (cart && Array.isArray(cart.items)) {
        for (const item of cart.items) {
          if (Number(item.variant_id) === variantId) count += Number(item.quantity) || 0;
        }
      }

      if (text) text.textContent = count > 0 ? `${count} ${count === 1 ? one : many} in your cart` : '';
      status.classList.toggle('vf-add-to-cart__status--empty', count === 0 && totalCount === 0);
    });

    // Header cart icon (Horizon's <cart-icon> component). The theme hides it on a
    // single-product store; vf.css shows it again once the cart has items.
    document.querySelectorAll('cart-icon').forEach((icon) => {
      if (typeof icon.renderCartBubble === 'function') {
        icon.renderCartBubble(totalCount);
      } else {
        icon.classList.toggle('header-actions__cart-icon--has-cart', totalCount > 0);
      }
    });
  }

  /* --------------------------------------------------------------- handlers */

  document.addEventListener('click', (event) => {
    const target = event.target.closest('[data-vf-qty-minus], [data-vf-qty-plus]');
    if (!target) return;
    const wrapper = target.closest('[data-vf-qty]');
    const input = wrapper && wrapper.querySelector('[data-vf-qty-input]');
    if (!input) return;
    event.preventDefault();
    const current = clampQty(input);
    input.value = String(target.hasAttribute('data-vf-qty-plus') ? current + 1 : current - 1);
    clampQty(input);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches && event.target.matches('[data-vf-qty-input]')) clampQty(event.target);
  });

  async function handleSubmit(event) {
    const form = event.target.closest(FORM);
    if (!form) return;
    event.preventDefault();

    const variantId = Number(form.querySelector('input[name="id"]').value);
    const qtyInput = form.querySelector('input[name="quantity"]');
    const quantity = qtyInput ? clampQty(qtyInput) : 1;
    const addUrl = form.dataset.cartAddUrl || '/cart/add.js';
    const cartUrl = form.dataset.cartUrl || '/cart.js';

    setBusy(form, true);

    try {
      await requestJSON(addUrl, {
        method: 'POST',
        body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
      });
      const cart = await requestJSON(cartUrl, { method: 'GET' });

      renderCart(cart);
      setBusy(form, false);
      flashAdded(form);
      if (qtyInput) qtyInput.value = '1';

      // Let analytics hooks (Shopify pixels, GA4 via theme app extensions) see the event.
      document.dispatchEvent(
        new CustomEvent('vf:add-to-cart', {
          detail: { variantId, quantity, cart },
          bubbles: true,
        })
      );
    } catch (error) {
      setBusy(form, false, error.message);
      // Fall back to the native form POST (return_to=/cart) on a second try.
      form.dataset.vfFallback = 'true';
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target.closest(FORM);
    if (!form) return;
    if (form.dataset.vfFallback === 'true') return; // let the browser submit normally
    handleSubmit(event);
  });

  // Keep the status line honest after a back/forward navigation (bfcache) or when
  // the cart was changed in another tab.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    const anyForm = document.querySelector(FORM);
    if (!anyForm) return;
    requestJSON(anyForm.dataset.cartUrl || '/cart.js', { method: 'GET' })
      .then(renderCart)
      .catch(() => {});
  });
})();
